import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ModalCloseX from "../../components/common/ModalCloseX";
import { useAuth } from "../../context/AuthContext";
import { listStaff } from "../../api/staffs";
import "../ReportsSectionCompact.css";
import {
  getClientAnalyticsOverview,
  getClientNewReturning,
  getClientLtvBands,
  getClientRfm,
  getClientAtRiskList,
  getClientRebookingRate,
  getClientAnalyticsTopClients,
} from "../../api/clientAnalytics";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
  TAB_BASE,
  TAB_ACTIVE,
  TAB_INACTIVE,
} from "../../ui/modalStyles";
import { getSalonTimezone } from "../../utils/salonTimezone";
import InfoTooltip from "../../components/common/InfoTooltip";
import SortableTh from "../../components/common/SortableTh";
import ReportHelpCard from "../../components/reports/ReportHelpCard";
import useHelpGuideNavigation from "../../hooks/useHelpGuideNavigation";
import useTableSort from "../../hooks/useTableSort";
import { CLIENT_ANALYTICS_TOOLTIPS } from "../../constants/reportTooltips";
import { REPORT_HELP } from "../../constants/reportHelpContent";

const DEFAULT_INACTIVE_DAYS = 60;

const formatDateISO = (date, timeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(date);
    } catch (err) {
      // fall back to local time
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (base, days) => {
  const next = new Date(base.getTime());
  next.setDate(next.getDate() + days);
  return next;
};

const formatMoney = (value) => {
  const amount = Number(value ?? 0);
  if (!Number.isFinite(amount)) return "$0.00";
  return `$${amount.toFixed(2)}`;
};

const formatNumber = (value, digits = 0) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "--";
  return num.toFixed(digits);
};

const normalizeTopN = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 5;
  const rounded = Math.round(parsed);
  return Math.min(50, Math.max(1, rounded));
};

const formatPercent = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "--";
  return `${num.toFixed(1)}%`;
};

const GROUP_BY_OPTIONS = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
];

const TOP_CLIENT_METRIC_OPTIONS = [
  { value: "revenue", label: "Revenue" },
  { value: "tips", label: "Tips" },
  { value: "frequency", label: "Frequency" },
];

const SORT_ORDER_OPTIONS = [
  { value: "desc", label: "Descending" },
  { value: "asc", label: "Ascending" },
];

const WINDOW_OPTIONS = [
  { value: 0, label: "0 days" },
  { value: 7, label: "7 days" },
  { value: 14, label: "14 days" },
  { value: 30, label: "30 days" },
];

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "topClients", label: "Top Clients" },
  { key: "newVsReturning", label: "New vs Returning" },
  { key: "ltvBands", label: "VIP / LTV Bands" },
  { key: "rfmSegments", label: "RFM Segments" },
  { key: "atRiskList", label: "Lapsed / At-Risk List" },
  { key: "rebookingRate", label: "Rebooking Rate" },
  { key: "cohortRetention", label: "Cohort Retention" },
  { key: "targetLists", label: "Target Lists" },
];

const StatTile = ({ label, value, hint }) => (
  <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
      {label}
    </div>
    <div className="mt-2 text-3xl font-semibold text-slate-900">{value}</div>
    {hint ? <div className="mt-1 text-sm text-slate-500">{hint}</div> : null}
  </div>
);

export default function ClientAnalyticsReport() {
  const navigate = useNavigate();
  const { currentSalon, user, loading: authLoading } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const helpGuide = REPORT_HELP.CLIENT_ANALYTICS;
  const resolvedSalonId = useMemo(() => {
    const raw = currentSalon?.id ?? currentSalon?.salon_id ?? user?.salon_id ?? null;
    if (raw === null || raw === undefined || raw === "") return null;
    const asNum = Number(raw);
    return Number.isFinite(asNum) ? asNum : null;
  }, [currentSalon, user?.salon_id]);
  const salonTimezone = useMemo(() => getSalonTimezone(currentSalon), [currentSalon]);

  const today = formatDateISO(new Date(), salonTimezone);
  const defaultStart = formatDateISO(addDays(new Date(), -6), salonTimezone);
  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    navigate("/manager/reports", { replace: true });
  };

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(today);
  const [employeeId, setEmployeeId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [includeRefunds, setIncludeRefunds] = useState(false);
  const [includeDiscounts, setIncludeDiscounts] = useState(true);
  const [groupBy, setGroupBy] = useState("week");
  const [activeInRange, setActiveInRange] = useState(true);
  const [windowDays, setWindowDays] = useState(14);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedSegment, setSelectedSegment] = useState("");
  const [topClientsMetric, setTopClientsMetric] = useState("revenue");
  const [topClientsTopN, setTopClientsTopN] = useState(5);
  const [topClientsSortOrder, setTopClientsSortOrder] = useState("desc");

  const [overview, setOverview] = useState(null);
  const [newReturningRows, setNewReturningRows] = useState([]);
  const [ltvBands, setLtvBands] = useState([]);
  const [rfmRows, setRfmRows] = useState([]);
  const [rfmClients, setRfmClients] = useState([]);
  const [atRiskRows, setAtRiskRows] = useState([]);
  const [rebooking, setRebooking] = useState(null);
  const [topClientsRows, setTopClientsRows] = useState([]);
  const [topClientsMeta, setTopClientsMeta] = useState(null);

  const [staffOptions, setStaffOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [topClientsLoading, setTopClientsLoading] = useState(false);
  const [topClientsError, setTopClientsError] = useState("");

  const sharedParams = useMemo(() => {
    const params = {
      start: startDate,
      end: endDate,
      include_refunds: includeRefunds,
      include_discounts: includeDiscounts,
    };
    if (employeeId) {
      params.employee_id = Number(employeeId);
    }
    if (categoryId) {
      params.category_id = Number(categoryId);
    }
    return params;
  }, [startDate, endDate, includeRefunds, includeDiscounts, employeeId, categoryId]);

  const fetchTopClients = useCallback(async () => {
    if (authLoading) return;
    if (!startDate || !endDate) {
      setTopClientsError("Select a valid start and end date.");
      return;
    }
    setTopClientsLoading(true);
    setTopClientsError("");
    const normalizedTopN = normalizeTopN(topClientsTopN);
    const payload = {
      start_date: startDate,
      end_date: endDate,
      metric: topClientsMetric,
      top_n: normalizedTopN,
      include_refunds: includeRefunds,
      include_discounts: includeDiscounts,
      sort_order: topClientsSortOrder,
    };
    if (employeeId) {
      payload.employee_id = Number(employeeId);
    }
    if (categoryId) {
      payload.service_category_id = Number(categoryId);
    }
    try {
      const res = await getClientAnalyticsTopClients(payload);
      setTopClientsRows(Array.isArray(res?.rows) ? res.rows : []);
      setTopClientsMeta(res?.meta || null);
    } catch (err) {
      setTopClientsRows([]);
      setTopClientsMeta(null);
      setTopClientsError("Couldn't load the report. Please try again.");
    } finally {
      setTopClientsLoading(false);
    }
  }, [
    authLoading,
    startDate,
    endDate,
    topClientsMetric,
    topClientsTopN,
    topClientsSortOrder,
    includeRefunds,
    includeDiscounts,
    employeeId,
    categoryId,
  ]);

  const loadStaff = useCallback(async () => {
    if (!resolvedSalonId) return;
    try {
      const staff = await listStaff({ salon_id: resolvedSalonId });
      const rows = Array.isArray(staff)
        ? staff
        : Array.isArray(staff?.rows)
        ? staff.rows
        : [];
      setStaffOptions(rows);
    } catch (err) {
      setStaffOptions([]);
    }
  }, [resolvedSalonId]);

  useEffect(() => {
    if (!authLoading && resolvedSalonId) {
      loadStaff();
    }
  }, [authLoading, resolvedSalonId, loadStaff]);

  const fetchAll = useCallback(async () => {
    if (authLoading) return;
    if (!resolvedSalonId) {
      setError(
        "Unable to determine your current salon. Please refresh or re-select your salon."
      );
      return;
    }
    if (!startDate || !endDate) {
      setError("Select a valid start and end date.");
      return;
    }
    setLoading(true);
    setError("");
    setSelectedSegment("");
    setRfmClients([]);
    try {
      const [
        overviewRes,
        newReturningRes,
        ltvRes,
        rfmRes,
        atRiskRes,
        rebookingRes,
      ] = await Promise.all([
        getClientAnalyticsOverview({
          ...sharedParams,
          inactive_days: DEFAULT_INACTIVE_DAYS,
        }),
        getClientNewReturning({ ...sharedParams, group_by: groupBy }),
        getClientLtvBands({ ...sharedParams, active_in_range: activeInRange }),
        getClientRfm(sharedParams),
        getClientAtRiskList({
          ...sharedParams,
          inactive_days: DEFAULT_INACTIVE_DAYS,
        }),
        getClientRebookingRate({ ...sharedParams, window_days: windowDays }),
      ]);
      setOverview(overviewRes || null);
      setNewReturningRows(Array.isArray(newReturningRes?.rows) ? newReturningRes.rows : []);
      setLtvBands(Array.isArray(ltvRes?.rows) ? ltvRes.rows : []);
      setRfmRows(Array.isArray(rfmRes?.rows) ? rfmRes.rows : []);
      setAtRiskRows(Array.isArray(atRiskRes?.rows) ? atRiskRes.rows : []);
      setRebooking(rebookingRes || null);
    } catch (err) {
      setOverview(null);
      setNewReturningRows([]);
      setLtvBands([]);
      setRfmRows([]);
      setRfmClients([]);
      setAtRiskRows([]);
      setRebooking(null);
      const detail = err?.response?.data?.detail;
      setError(detail || "Unable to load client analytics.");
    } finally {
      setLoading(false);
    }
    fetchTopClients();
  }, [
    authLoading,
    resolvedSalonId,
    startDate,
    endDate,
    sharedParams,
    groupBy,
    activeInRange,
    windowDays,
    fetchTopClients,
  ]);

  const fetchRfmSegment = useCallback(
    async (segmentName) => {
      if (!segmentName) return;
      if (authLoading || !resolvedSalonId || !startDate || !endDate) return;
      setLoading(true);
      setError("");
      try {
        const res = await getClientRfm({ ...sharedParams, segment: segmentName });
        setRfmRows(Array.isArray(res?.rows) ? res.rows : []);
        setRfmClients(Array.isArray(res?.clients) ? res.clients : []);
        setSelectedSegment(segmentName);
      } catch (err) {
        setRfmClients([]);
        const detail = err?.response?.data?.detail;
        setError(detail || "Unable to load segment clients.");
      } finally {
        setLoading(false);
      }
    },
    [authLoading, resolvedSalonId, startDate, endDate, sharedParams]
  );

  useEffect(() => {
    if (!authLoading && resolvedSalonId) {
      fetchAll();
    }
  }, [authLoading, resolvedSalonId, fetchAll]);

  const resetFilters = () => {
    setStartDate(defaultStart);
    setEndDate(today);
    setEmployeeId("");
    setCategoryId("");
    setIncludeRefunds(false);
    setIncludeDiscounts(true);
    setGroupBy("week");
    setActiveInRange(true);
    setWindowDays(14);
    setSelectedSegment("");
    setRfmClients([]);
    setTopClientsMetric("revenue");
    setTopClientsTopN(5);
    setTopClientsSortOrder("desc");
    setTopClientsRows([]);
    setTopClientsMeta(null);
    setTopClientsError("");
  };

  const togglePillClass = (active) => `${TAB_BASE} ${active ? TAB_ACTIVE : TAB_INACTIVE}`;

  const renderFieldLabel = (label, tooltipKey) => (
    <div className="flex items-center gap-1.5">
      <label className={LABEL_TEXT}>{label}</label>
      {tooltipKey ? (
        <InfoTooltip text={CLIENT_ANALYTICS_TOOLTIPS[tooltipKey]} label={label} />
      ) : null}
    </div>
  );

  const renderInlineLabel = (label, tooltipKey) => (
    <div className="flex items-center gap-1.5">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </span>
      {tooltipKey ? (
        <InfoTooltip text={CLIENT_ANALYTICS_TOOLTIPS[tooltipKey]} label={label} />
      ) : null}
    </div>
  );

  const goToClientsByService = () => {
    const params = new URLSearchParams();
    params.set("tab", "clientsByService");
    params.set("start", startDate);
    params.set("end", endDate);
    if (employeeId) params.set("employee_id", employeeId);
    if (categoryId) params.set("category_id", categoryId);
    params.set("include_refunds", String(includeRefunds));
    params.set("include_discounts", String(includeDiscounts));
    navigate(`/reports/service-analytics?${params.toString()}`);
  };

  const tiles = overview?.tiles || {};
  const rebookingOverall = rebooking?.overall || {};
  const rebookingByEmployee = Array.isArray(rebooking?.by_employee)
    ? rebooking.by_employee
    : [];
  const atRiskHasContact = atRiskRows.some((row) => row.phone || row.email);
  const rfmHasContact = rfmClients.some((row) => row.phone || row.email);
  const topClientsMetricLabel =
    topClientsMetric === "tips"
      ? "Tips"
      : topClientsMetric === "frequency"
      ? "Visits"
      : "Revenue";

  const newReturningColumns = useMemo(
    () => ({
      period_start: { type: "date", getValue: (row) => row?.period_start },
      new_clients: { type: "number", getValue: (row) => row?.new_clients },
      returning_clients: {
        type: "number",
        getValue: (row) => row?.returning_clients,
      },
      revenue_new_net: { type: "number", getValue: (row) => row?.revenue_new_net },
      revenue_returning_net: {
        type: "number",
        getValue: (row) => row?.revenue_returning_net,
      },
    }),
    []
  );

  const newReturningSort = useTableSort({
    rows: newReturningRows,
    columns: newReturningColumns,
    fallbackKey: "period_start",
  });

  const ltvColumns = useMemo(
    () => ({
      band_label: { type: "text", getValue: (row) => row?.band_label || "" },
      client_count: { type: "number", getValue: (row) => row?.client_count },
      total_lifetime_net: {
        type: "number",
        getValue: (row) => row?.total_lifetime_net,
      },
      avg_visits_lifetime: {
        type: "number",
        getValue: (row) => row?.avg_visits_lifetime,
      },
      last_visit_date: { type: "date", getValue: (row) => row?.last_visit_date },
    }),
    []
  );

  const ltvSort = useTableSort({
    rows: ltvBands,
    columns: ltvColumns,
    fallbackKey: "band_label",
  });

  const rfmColumns = useMemo(
    () => ({
      segment_name: { type: "text", getValue: (row) => row?.segment_name || "" },
      client_count: { type: "number", getValue: (row) => row?.client_count },
      avg_recency_days: {
        type: "number",
        getValue: (row) => row?.avg_recency_days,
      },
      avg_frequency_180d: {
        type: "number",
        getValue: (row) => row?.avg_frequency_180d,
      },
      avg_monetary_180d: {
        type: "number",
        getValue: (row) => row?.avg_monetary_180d,
      },
      action: { type: "text", getValue: (row) => row?.segment_name || "" },
    }),
    []
  );

  const rfmSort = useTableSort({
    rows: rfmRows,
    columns: rfmColumns,
    fallbackKey: "segment_name",
  });

  const rfmClientsColumns = useMemo(
    () => ({
      client_name: { type: "text", getValue: (row) => row?.client_name || "" },
      recency_days: { type: "number", getValue: (row) => row?.recency_days },
      frequency_180d: {
        type: "number",
        getValue: (row) => row?.frequency_180d,
      },
      monetary_180d: {
        type: "number",
        getValue: (row) => row?.monetary_180d,
      },
      last_visit_date: { type: "date", getValue: (row) => row?.last_visit_date },
      phone: { type: "text", getValue: (row) => row?.phone || "" },
      email: { type: "text", getValue: (row) => row?.email || "" },
    }),
    []
  );

  const rfmClientsSort = useTableSort({
    rows: rfmClients,
    columns: rfmClientsColumns,
    fallbackKey: "client_name",
  });

  const atRiskColumns = useMemo(
    () => ({
      client_name: { type: "text", getValue: (row) => row?.client_name || "" },
      phone: { type: "text", getValue: (row) => row?.phone || "" },
      email: { type: "text", getValue: (row) => row?.email || "" },
      last_visit_date: { type: "date", getValue: (row) => row?.last_visit_date },
      last_service_name: {
        type: "text",
        getValue: (row) => row?.last_service_name || "",
      },
      last_employee_name: {
        type: "text",
        getValue: (row) => row?.last_employee_name || "",
      },
      lifetime_net_spend: {
        type: "number",
        getValue: (row) => row?.lifetime_net_spend,
      },
      visits_last_12_months: {
        type: "number",
        getValue: (row) => row?.visits_last_12_months,
      },
      recency_days: { type: "number", getValue: (row) => row?.recency_days },
    }),
    []
  );

  const atRiskSort = useTableSort({
    rows: atRiskRows,
    columns: atRiskColumns,
    fallbackKey: "client_name",
  });

  const rebookingColumns = useMemo(
    () => ({
      employee_name: { type: "text", getValue: (row) => row?.employee_name || "" },
      opportunities: { type: "number", getValue: (row) => row?.opportunities },
      rebooked_count: { type: "number", getValue: (row) => row?.rebooked_count },
      rebooking_rate_percent: {
        type: "percent",
        getValue: (row) => row?.rebooking_rate_percent,
      },
    }),
    []
  );

  const rebookingSort = useTableSort({
    rows: rebookingByEmployee,
    columns: rebookingColumns,
    fallbackKey: "employee_name",
  });

  const topClientsColumns = useMemo(
    () => ({
      rank: { type: "number", getValue: (row) => row?.rank },
      client_name: { type: "text", getValue: (row) => row?.client_name || "" },
      metric_value: {
        type: "number",
        getValue: (row) => {
          if (topClientsMetric === "tips") return row?.tips ?? 0;
          if (topClientsMetric === "frequency") return row?.visits ?? 0;
          return row?.revenue ?? 0;
        },
      },
      ticket_count: { type: "number", getValue: (row) => row?.ticket_count },
      tip_count: { type: "number", getValue: (row) => row?.tip_count },
      avg_spend_per_visit: {
        type: "number",
        getValue: (row) => row?.avg_spend_per_visit,
      },
    }),
    [topClientsMetric]
  );

  const topClientsSort = useTableSort({
    rows: topClientsRows,
    columns: topClientsColumns,
    defaultSortBy: "rank",
    defaultSortDir: "asc",
    fallbackKey: "client_name",
  });

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4 relative">
            <div className="text-xl md:text-2xl font-semibold text-white">
              Client Analytics
            </div>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>
          <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
            <div
              id="client-analytics-overview"
              className={getSectionClassName("client-analytics-overview")}
            >
              <ReportHelpCard
                title={helpGuide.title}
                subtitle={helpGuide.subtitle}
                sections={helpGuide.sections}
                troubleshooting={helpGuide.troubleshooting}
                storageKey="report_help_client_analytics"
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>
        {error && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
            {error}
          </div>
        )}

        <div className="sticky top-0 z-10 bg-white pb-4">
          <div
            id="client-analytics-filters"
            className={`rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4 ${getSectionClassName(
              "client-analytics-filters"
            )}`}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <div>
                {renderFieldLabel("Start Date", "start_date")}
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className={`${FIELD_CLASS} w-full`}
                />
              </div>
              <div>
                {renderFieldLabel("End Date", "end_date")}
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className={`${FIELD_CLASS} w-full`}
                />
              </div>
              <div>
                {renderFieldLabel("Employee", "employee")}
                <select
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value)}
                  className={`${FIELD_CLASS} w-full`}
                >
                  <option value="">All employees</option>
                  {staffOptions.map((staff) => (
                    <option key={staff.id} value={staff.id}>
                      {staff.display_name || `Staff ${staff.id}`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                {renderFieldLabel("Service Category", "category")}
                <select
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  className={`${FIELD_CLASS} w-full`}
                >
                  <option value="">All categories</option>
                </select>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                {renderInlineLabel("Refunds", "include_refunds")}
                <button
                  type="button"
                  className={togglePillClass(includeRefunds)}
                  onClick={() => setIncludeRefunds((prev) => !prev)}
                >
                  Include refunds
                </button>
              </div>
              <div className="flex items-center gap-2">
                {renderInlineLabel("Discounts", "include_discounts")}
                <button
                  type="button"
                  className={togglePillClass(includeDiscounts)}
                  onClick={() => setIncludeDiscounts((prev) => !prev)}
                >
                  Include discounts
                </button>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <button type="button" onClick={resetFilters} className={BTN_OUTLINE}>
                  Clear
                </button>
                <button
                  type="button"
                  onClick={fetchAll}
                  className={BTN_PRIMARY}
                  disabled={loading}
                >
                  {loading ? "Loading..." : "Run Report"}
                </button>
              </div>
            </div>
          </div>

        <div className="mt-4 flex flex-wrap gap-2">
            {TABS.map((tab) => {
              const tooltip = CLIENT_ANALYTICS_TOOLTIPS[`tab_${tab.key}`];
              return (
                <div key={tab.key} className="flex items-center gap-1.5">
                  <button
                    type="button"
                    className={`${TAB_BASE} ${
                      activeTab === tab.key ? TAB_ACTIVE : TAB_INACTIVE
                    }`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                  {tooltip ? <InfoTooltip text={tooltip} label={tab.label} /> : null}
                </div>
              );
            })}
          </div>
        </div>

        <div
          id="client-analytics-details"
          className={`space-y-6 ${getSectionClassName("client-analytics-details")}`}
        >
          <div
            id="client-analytics-export"
            className={`rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600 ${getSectionClassName(
              "client-analytics-export"
            )}`}
          >
            Export is not available on this report.
          </div>
          {activeTab === "overview" && (
            <div
              id="client-analytics-kpis"
              className={`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 ${getSectionClassName(
                "client-analytics-kpis"
              )}`}
            >
              <StatTile
                label="Unique Clients"
                value={formatNumber(tiles.unique_clients, 0)}
              />
              <StatTile label="New Clients" value={formatNumber(tiles.new_clients, 0)} />
              <StatTile
                label="Returning Clients"
                value={formatNumber(tiles.returning_clients, 0)}
              />
              <StatTile
                label="Repeat Rate"
                value={formatPercent(tiles.repeat_rate_percent)}
              />
              <StatTile
                label="Avg Visits / Client"
                value={formatNumber(tiles.avg_visits_per_client, 2)}
              />
              <StatTile
                label="Avg Spend / Client (Net)"
                value={formatMoney(tiles.avg_spend_per_client_net)}
              />
              <StatTile
                label="At-Risk Clients"
                value={formatNumber(tiles.at_risk_count, 0)}
                hint={`${DEFAULT_INACTIVE_DAYS}-day inactivity`}
              />
            </div>
          )}
          {activeTab === "topClients" && (
            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    {renderFieldLabel("Metric", "top_clients_metric")}
                    <select
                      value={topClientsMetric}
                      onChange={(e) => setTopClientsMetric(e.target.value)}
                      className={`${FIELD_CLASS} w-full`}
                    >
                      {TOP_CLIENT_METRIC_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    {renderFieldLabel("Top N", "top_clients_n")}
                    <input
                      type="number"
                      min={1}
                      max={50}
                      value={topClientsTopN}
                      onChange={(e) =>
                        setTopClientsTopN(normalizeTopN(e.target.value))
                      }
                      className={`${FIELD_CLASS} w-full`}
                    />
                  </div>
                  <div>
                    {renderFieldLabel("Sort Order", "top_clients_sort_order")}
                    <select
                      value={topClientsSortOrder}
                      onChange={(e) => setTopClientsSortOrder(e.target.value)}
                      className={`${FIELD_CLASS} w-full`}
                    >
                      {SORT_ORDER_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-3 text-xs text-slate-500">
                  Use Run Report to refresh the ranking.
                </div>
              </div>

              {topClientsError && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                  {topClientsError}
                </div>
              )}

              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
                  <h3 className="text-lg font-semibold text-slate-900">
                    Top {topClientsMeta?.top_n ?? topClientsTopN} Clients by{" "}
                    {topClientsMetricLabel}
                  </h3>
                  <div className="text-sm text-slate-500">
                    {topClientsSort.sortedRows.length
                      ? `${topClientsSort.sortedRows.length} clients`
                      : ""}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm report-table">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <SortableTh
                          label="Rank"
                          sortKey="rank"
                          sortState={topClientsSort.sortState}
                          onSort={topClientsSort.handleSort}
                          tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_rank}
                          className="px-4 py-3 report-th-center"
                          align="center"
                        />
                        <SortableTh
                          label="Client"
                          sortKey="client_name"
                          sortState={topClientsSort.sortState}
                          onSort={topClientsSort.handleSort}
                          tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_client}
                          className="px-4 py-3 report-th-left"
                          align="left"
                        />
                        <SortableTh
                          label={topClientsMetricLabel}
                          sortKey="metric_value"
                          sortState={topClientsSort.sortState}
                          onSort={topClientsSort.handleSort}
                          tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_top_metric}
                          className="px-4 py-3 report-th-center"
                          align="center"
                        />
                        {topClientsMetric !== "frequency" && (
                          <SortableTh
                            label="Tickets"
                            sortKey="ticket_count"
                            sortState={topClientsSort.sortState}
                            onSort={topClientsSort.handleSort}
                            tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_ticket_count}
                            className="px-4 py-3 report-th-center"
                            align="center"
                          />
                        )}
                        {topClientsMetric === "tips" && (
                          <SortableTh
                            label="Tip Count"
                            sortKey="tip_count"
                            sortState={topClientsSort.sortState}
                            onSort={topClientsSort.handleSort}
                            tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_tip_count}
                            className="px-4 py-3 report-th-center"
                            align="center"
                          />
                        )}
                        {topClientsMetric === "revenue" && (
                          <SortableTh
                            label="Avg / Visit"
                            sortKey="avg_spend_per_visit"
                            sortState={topClientsSort.sortState}
                            onSort={topClientsSort.handleSort}
                            tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_avg_spend_visit}
                            className="px-4 py-3 report-th-center"
                            align="center"
                          />
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {topClientsSort.sortedRows.map((row) => (
                        <tr key={`top-client-${row.client_id}-${row.rank}`} className="border-t border-slate-100">
                          <td className="px-4 py-3 report-td-center text-slate-700">
                            {formatNumber(row.rank, 0)}
                          </td>
                          <td className="px-4 py-3 report-td-left text-slate-700">
                            {row.client_name}
                          </td>
                          <td className="px-4 py-3 report-td-center text-slate-700">
                            {topClientsMetric === "frequency"
                              ? formatNumber(row.visits, 0)
                              : formatMoney(
                                  topClientsMetric === "tips"
                                    ? row.tips
                                    : row.revenue
                                )}
                          </td>
                          {topClientsMetric !== "frequency" && (
                            <td className="px-4 py-3 report-td-center text-slate-700">
                              {formatNumber(row.ticket_count, 0)}
                            </td>
                          )}
                          {topClientsMetric === "tips" && (
                            <td className="px-4 py-3 report-td-center text-slate-700">
                              {formatNumber(row.tip_count, 0)}
                            </td>
                          )}
                          {topClientsMetric === "revenue" && (
                            <td className="px-4 py-3 report-td-center text-slate-700">
                              {formatMoney(row.avg_spend_per_visit)}
                            </td>
                          )}
                        </tr>
                      ))}
                      {!topClientsSort.sortedRows.length && (
                        <tr>
                          <td
                            colSpan={topClientsMetric === "frequency" ? 3 : 5}
                            className="px-4 py-6 text-center text-slate-500"
                          >
                            {topClientsLoading ? "Loading..." : "No results found."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          {activeTab === "newVsReturning" && (
            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
                <div className="max-w-xs">
                  {renderFieldLabel("Group By", "group_by")}
                  <select
                    value={groupBy}
                    onChange={(e) => setGroupBy(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  >
                    {GROUP_BY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-900">
                    New vs Returning Clients
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm report-table">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <SortableTh
                          label="Period Start"
                          sortKey="period_start"
                          sortState={newReturningSort.sortState}
                          onSort={newReturningSort.handleSort}
                          tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_period_start}
                          className="px-4 py-3 report-th-left"
                          align="left"
                        />
                        <SortableTh
                          label="New Clients"
                          sortKey="new_clients"
                          sortState={newReturningSort.sortState}
                          onSort={newReturningSort.handleSort}
                          tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_new_clients}
                          className="px-4 py-3 report-th-center"
                          align="center"
                        />
                        <SortableTh
                          label="Returning Clients"
                          sortKey="returning_clients"
                          sortState={newReturningSort.sortState}
                          onSort={newReturningSort.handleSort}
                          tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_returning_clients}
                          className="px-4 py-3 report-th-center"
                          align="center"
                        />
                        <SortableTh
                          label="Revenue (New Net)"
                          sortKey="revenue_new_net"
                          sortState={newReturningSort.sortState}
                          onSort={newReturningSort.handleSort}
                          tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_revenue_new_net}
                          className="px-4 py-3 report-th-center"
                          align="center"
                        />
                        <SortableTh
                          label="Revenue (Returning Net)"
                          sortKey="revenue_returning_net"
                          sortState={newReturningSort.sortState}
                          onSort={newReturningSort.handleSort}
                          tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_revenue_returning_net}
                          className="px-4 py-3 report-th-center"
                          align="center"
                        />
                      </tr>
                    </thead>
                    <tbody>
                      {newReturningSort.sortedRows.map((row) => (
                        <tr key={row.period_start} className="border-t border-slate-100">
                          <td className="px-4 py-3 report-td-left text-slate-700">{row.period_start}</td>
                          <td className="px-4 py-3 report-td-center text-slate-700">
                            {formatNumber(row.new_clients, 0)}
                          </td>
                          <td className="px-4 py-3 report-td-center text-slate-700">
                            {formatNumber(row.returning_clients, 0)}
                          </td>
                          <td className="px-4 py-3 report-td-center text-slate-700">
                            {formatMoney(row.revenue_new_net)}
                          </td>
                          <td className="px-4 py-3 report-td-center text-slate-700">
                            {formatMoney(row.revenue_returning_net)}
                          </td>
                        </tr>
                      ))}
                      {!newReturningSort.sortedRows.length && (
                        <tr>
                          <td colSpan={5} className="px-4 py-6 text-center text-slate-500">
                            {loading ? "Loading..." : "No client trend data found."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          {activeTab === "ltvBands" && (
            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
                <div className="flex flex-wrap items-center gap-2">
                  {renderInlineLabel("Client Scope", "active_in_range")}
                  <button
                    type="button"
                    className={togglePillClass(activeInRange)}
                    onClick={() => setActiveInRange((prev) => !prev)}
                  >
                    Only clients active in range
                  </button>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-900">Lifetime Value Bands</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm report-table">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <SortableTh
                          label="Band"
                          sortKey="band_label"
                          sortState={ltvSort.sortState}
                          onSort={ltvSort.handleSort}
                          tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_band}
                          className="px-4 py-3 report-th-left"
                          align="left"
                        />
                        <SortableTh
                          label="Clients"
                          sortKey="client_count"
                          sortState={ltvSort.sortState}
                          onSort={ltvSort.handleSort}
                          tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_clients}
                          className="px-4 py-3 report-th-center"
                          align="center"
                        />
                        <SortableTh
                          label="Total Lifetime Net"
                          sortKey="total_lifetime_net"
                          sortState={ltvSort.sortState}
                          onSort={ltvSort.handleSort}
                          tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_total_lifetime_net}
                          className="px-4 py-3 report-th-center"
                          align="center"
                        />
                        <SortableTh
                          label="Avg Visits (Lifetime)"
                          sortKey="avg_visits_lifetime"
                          sortState={ltvSort.sortState}
                          onSort={ltvSort.handleSort}
                          tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_avg_visits_lifetime}
                          className="px-4 py-3 report-th-center"
                          align="center"
                        />
                        <SortableTh
                          label="Last Visit"
                          sortKey="last_visit_date"
                          sortState={ltvSort.sortState}
                          onSort={ltvSort.handleSort}
                          tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_last_visit}
                          className="px-4 py-3 report-th-left"
                          align="left"
                        />
                      </tr>
                    </thead>
                    <tbody>
                      {ltvSort.sortedRows.map((row) => (
                        <tr key={row.band_label} className="border-t border-slate-100">
                          <td className="px-4 py-3 report-td-left text-slate-700">{row.band_label}</td>
                          <td className="px-4 py-3 report-td-center text-slate-700">
                            {formatNumber(row.client_count, 0)}
                          </td>
                          <td className="px-4 py-3 report-td-center text-slate-700">
                            {formatMoney(row.total_lifetime_net)}
                          </td>
                          <td className="px-4 py-3 report-td-center text-slate-700">
                            {formatNumber(row.avg_visits_lifetime, 2)}
                          </td>
                          <td className="px-4 py-3 report-td-left text-slate-700">
                            {row.last_visit_date || "--"}
                          </td>
                        </tr>
                      ))}
                      {!ltvSort.sortedRows.length && (
                        <tr>
                          <td colSpan={5} className="px-4 py-6 text-center text-slate-500">
                            {loading ? "Loading..." : "No lifetime value data found."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          {activeTab === "rfmSegments" && (
            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-900">RFM Segments</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm report-table">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <SortableTh
                          label="Segment"
                          sortKey="segment_name"
                          sortState={rfmSort.sortState}
                          onSort={rfmSort.handleSort}
                          tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_segment}
                          className="px-4 py-3 report-th-left"
                          align="left"
                        />
                        <SortableTh
                          label="Clients"
                          sortKey="client_count"
                          sortState={rfmSort.sortState}
                          onSort={rfmSort.handleSort}
                          tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_clients}
                          className="px-4 py-3 report-th-center"
                          align="center"
                        />
                        <SortableTh
                          label="Avg Recency (days)"
                          sortKey="avg_recency_days"
                          sortState={rfmSort.sortState}
                          onSort={rfmSort.handleSort}
                          tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_avg_recency}
                          className="px-4 py-3 report-th-center"
                          align="center"
                        />
                        <SortableTh
                          label="Avg Frequency (180d)"
                          sortKey="avg_frequency_180d"
                          sortState={rfmSort.sortState}
                          onSort={rfmSort.handleSort}
                          tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_avg_frequency}
                          className="px-4 py-3 report-th-center"
                          align="center"
                        />
                        <SortableTh
                          label="Avg Monetary (180d)"
                          sortKey="avg_monetary_180d"
                          sortState={rfmSort.sortState}
                          onSort={rfmSort.handleSort}
                          tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_avg_monetary}
                          className="px-4 py-3 report-th-center"
                          align="center"
                        />
                        <SortableTh
                          label="Action"
                          sortKey="action"
                          sortState={rfmSort.sortState}
                          onSort={rfmSort.handleSort}
                          tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_action}
                          className="px-4 py-3 report-th-center"
                          align="center"
                        />
                      </tr>
                    </thead>
                    <tbody>
                      {rfmSort.sortedRows.map((row) => (
                        <tr key={row.segment_name} className="border-t border-slate-100">
                          <td className="px-4 py-3 report-td-left text-slate-700">{row.segment_name}</td>
                          <td className="px-4 py-3 report-td-center text-slate-700">
                            {formatNumber(row.client_count, 0)}
                          </td>
                          <td className="px-4 py-3 report-td-center text-slate-700">
                            {formatNumber(row.avg_recency_days, 1)}
                          </td>
                          <td className="px-4 py-3 report-td-center text-slate-700">
                            {formatNumber(row.avg_frequency_180d, 2)}
                          </td>
                          <td className="px-4 py-3 report-td-center text-slate-700">
                            {formatMoney(row.avg_monetary_180d)}
                          </td>
                          <td className="px-4 py-3 report-td-center">
                            <button
                              type="button"
                              className={`${BTN_OUTLINE} h-9 md:h-9 px-4 text-sm md:text-sm`}
                              onClick={() => fetchRfmSegment(row.segment_name)}
                            >
                              View Clients
                            </button>
                          </td>
                        </tr>
                      ))}
                      {!rfmSort.sortedRows.length && (
                        <tr>
                          <td colSpan={6} className="px-4 py-6 text-center text-slate-500">
                            {loading ? "Loading..." : "No RFM segment data found."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {selectedSegment && (
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
                    <h3 className="text-lg font-semibold text-slate-900">
                      {selectedSegment} Clients
                    </h3>
                    <div className="text-sm text-slate-500">
                      {rfmClients.length ? `${rfmClients.length} clients` : ""}
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm report-table">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr>
                          <SortableTh
                            label="Client"
                            sortKey="client_name"
                            sortState={rfmClientsSort.sortState}
                            onSort={rfmClientsSort.handleSort}
                            tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_client}
                            className="px-4 py-3 report-th-left"
                            align="left"
                          />
                          <SortableTh
                            label="Recency"
                            sortKey="recency_days"
                            sortState={rfmClientsSort.sortState}
                            onSort={rfmClientsSort.handleSort}
                            tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_recency_days}
                            className="px-4 py-3 report-th-center"
                            align="center"
                          />
                          <SortableTh
                            label="Frequency (180d)"
                            sortKey="frequency_180d"
                            sortState={rfmClientsSort.sortState}
                            onSort={rfmClientsSort.handleSort}
                            tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_frequency_180d}
                            className="px-4 py-3 report-th-center"
                            align="center"
                          />
                          <SortableTh
                            label="Monetary (180d)"
                            sortKey="monetary_180d"
                            sortState={rfmClientsSort.sortState}
                            onSort={rfmClientsSort.handleSort}
                            tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_monetary_180d}
                            className="px-4 py-3 report-th-center"
                            align="center"
                          />
                          <SortableTh
                            label="Last Visit"
                            sortKey="last_visit_date"
                            sortState={rfmClientsSort.sortState}
                            onSort={rfmClientsSort.handleSort}
                            tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_last_visit}
                            className="px-4 py-3 report-th-left"
                            align="left"
                          />
                          {rfmHasContact && (
                            <>
                              <SortableTh
                                label="Phone"
                                sortKey="phone"
                                sortState={rfmClientsSort.sortState}
                                onSort={rfmClientsSort.handleSort}
                                tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_phone}
                                className="px-4 py-3 report-th-left"
                                align="left"
                              />
                              <SortableTh
                                label="Email"
                                sortKey="email"
                                sortState={rfmClientsSort.sortState}
                                onSort={rfmClientsSort.handleSort}
                                tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_email}
                                className="px-4 py-3 report-th-left"
                                align="left"
                              />
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {rfmClientsSort.sortedRows.map((row) => (
                          <tr key={`${selectedSegment}-${row.client_id}`} className="border-t border-slate-100">
                            <td className="px-4 py-3 report-td-left text-slate-700">{row.client_name}</td>
                            <td className="px-4 py-3 report-td-center text-slate-700">
                              {formatNumber(row.recency_days, 0)}
                            </td>
                            <td className="px-4 py-3 report-td-center text-slate-700">
                              {formatNumber(row.frequency_180d, 0)}
                            </td>
                            <td className="px-4 py-3 report-td-center text-slate-700">
                              {formatMoney(row.monetary_180d)}
                            </td>
                            <td className="px-4 py-3 report-td-left text-slate-700">
                              {row.last_visit_date || "--"}
                            </td>
                            {rfmHasContact && (
                              <>
                                <td className="px-4 py-3 report-td-left text-slate-700">{row.phone || "--"}</td>
                                <td className="px-4 py-3 report-td-left text-slate-700">{row.email || "--"}</td>
                              </>
                            )}
                          </tr>
                        ))}
                        {!rfmClientsSort.sortedRows.length && (
                          <tr>
                            <td
                              colSpan={rfmHasContact ? 7 : 5}
                              className="px-4 py-6 text-center text-slate-500"
                            >
                              {loading ? "Loading..." : "No clients found for this segment."}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
          {activeTab === "atRiskList" && (
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
                <h3 className="text-lg font-semibold text-slate-900">At-Risk Clients</h3>
                <div className="text-sm text-slate-500">
                  {formatNumber(DEFAULT_INACTIVE_DAYS, 0)}+ days inactive
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm report-table">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <SortableTh
                        label="Client"
                        sortKey="client_name"
                        sortState={atRiskSort.sortState}
                        onSort={atRiskSort.handleSort}
                        tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_client}
                        className="px-4 py-3 report-th-left"
                        align="left"
                      />
                      {atRiskHasContact && (
                        <>
                          <SortableTh
                            label="Phone"
                            sortKey="phone"
                            sortState={atRiskSort.sortState}
                            onSort={atRiskSort.handleSort}
                            tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_phone}
                            className="px-4 py-3 report-th-left"
                            align="left"
                          />
                          <SortableTh
                            label="Email"
                            sortKey="email"
                            sortState={atRiskSort.sortState}
                            onSort={atRiskSort.handleSort}
                            tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_email}
                            className="px-4 py-3 report-th-left"
                            align="left"
                          />
                        </>
                      )}
                      <SortableTh
                        label="Last Visit"
                        sortKey="last_visit_date"
                        sortState={atRiskSort.sortState}
                        onSort={atRiskSort.handleSort}
                        tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_last_visit}
                        className="px-4 py-3 report-th-left"
                        align="left"
                      />
                      <SortableTh
                        label="Last Service"
                        sortKey="last_service_name"
                        sortState={atRiskSort.sortState}
                        onSort={atRiskSort.handleSort}
                        tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_last_service}
                        className="px-4 py-3 report-th-left"
                        align="left"
                      />
                      <SortableTh
                        label="Last Employee"
                        sortKey="last_employee_name"
                        sortState={atRiskSort.sortState}
                        onSort={atRiskSort.handleSort}
                        tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_last_employee}
                        className="px-4 py-3 report-th-left"
                        align="left"
                      />
                      <SortableTh
                        label="Lifetime Net"
                        sortKey="lifetime_net_spend"
                        sortState={atRiskSort.sortState}
                        onSort={atRiskSort.handleSort}
                        tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_lifetime_net}
                        className="px-4 py-3 report-th-center"
                        align="center"
                      />
                      <SortableTh
                        label="Visits (12m)"
                        sortKey="visits_last_12_months"
                        sortState={atRiskSort.sortState}
                        onSort={atRiskSort.handleSort}
                        tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_visits_12m}
                        className="px-4 py-3 report-th-center"
                        align="center"
                      />
                      <SortableTh
                        label="Recency"
                        sortKey="recency_days"
                        sortState={atRiskSort.sortState}
                        onSort={atRiskSort.handleSort}
                        tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_recency_days}
                        className="px-4 py-3 report-th-center"
                        align="center"
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {atRiskSort.sortedRows.map((row) => (
                      <tr key={`atrisk-${row.client_id}`} className="border-t border-slate-100">
                        <td className="px-4 py-3 report-td-left text-slate-700">{row.client_name}</td>
                        {atRiskHasContact && (
                          <>
                            <td className="px-4 py-3 report-td-left text-slate-700">{row.phone || "--"}</td>
                            <td className="px-4 py-3 report-td-left text-slate-700">{row.email || "--"}</td>
                          </>
                        )}
                        <td className="px-4 py-3 report-td-left text-slate-700">
                          {row.last_visit_date || "--"}
                        </td>
                        <td className="px-4 py-3 report-td-left text-slate-700">{row.last_service_name || "--"}</td>
                        <td className="px-4 py-3 report-td-left text-slate-700">{row.last_employee_name || "--"}</td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatMoney(row.lifetime_net_spend)}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatNumber(row.visits_last_12_months, 0)}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatNumber(row.recency_days, 0)}
                        </td>
                      </tr>
                    ))}
                    {!atRiskSort.sortedRows.length && (
                      <tr>
                        <td
                          colSpan={atRiskHasContact ? 9 : 7}
                          className="px-4 py-6 text-center text-slate-500"
                        >
                          {loading ? "Loading..." : "No at-risk clients found."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {activeTab === "rebookingRate" && (
            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
                <div className="max-w-xs">
                  {renderFieldLabel("Rebooking Window", "rebooking_window")}
                  <select
                    value={windowDays}
                    onChange={(e) => setWindowDays(Number(e.target.value))}
                    className={`${FIELD_CLASS} w-full`}
                  >
                    {WINDOW_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatTile
                  label="Rebooking Rate"
                  value={formatPercent(rebookingOverall.rebooking_rate_percent)}
                />
                <StatTile
                  label="Opportunities"
                  value={formatNumber(rebookingOverall.opportunities, 0)}
                />
                <StatTile
                  label="Rebooked"
                  value={formatNumber(rebookingOverall.rebooked_count, 0)}
                />
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-900">Rebooking by Employee</h3>
                </div>
                <div className="overflow-x-auto">
                <table className="min-w-full text-sm report-table">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <SortableTh
                        label="Employee"
                        sortKey="employee_name"
                        sortState={rebookingSort.sortState}
                        onSort={rebookingSort.handleSort}
                        tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_employee}
                        className="px-4 py-3 report-th-left"
                        align="left"
                      />
                      <SortableTh
                        label="Opportunities"
                        sortKey="opportunities"
                        sortState={rebookingSort.sortState}
                        onSort={rebookingSort.handleSort}
                        tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_opportunities}
                        className="px-4 py-3 report-th-center"
                        align="center"
                      />
                      <SortableTh
                        label="Rebooked"
                        sortKey="rebooked_count"
                        sortState={rebookingSort.sortState}
                        onSort={rebookingSort.handleSort}
                        tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_rebooked}
                        className="px-4 py-3 report-th-center"
                        align="center"
                      />
                      <SortableTh
                        label="Rate"
                        sortKey="rebooking_rate_percent"
                        sortState={rebookingSort.sortState}
                        onSort={rebookingSort.handleSort}
                        tooltip={CLIENT_ANALYTICS_TOOLTIPS.column_rebook_rate}
                        className="px-4 py-3 report-th-center"
                        align="center"
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {rebookingSort.sortedRows.map((row) => (
                      <tr key={`rebook-${row.employee_id ?? "none"}`} className="border-t border-slate-100">
                        <td className="px-4 py-3 report-td-left text-slate-700">{row.employee_name}</td>
                          <td className="px-4 py-3 report-td-center text-slate-700">
                            {formatNumber(row.opportunities, 0)}
                          </td>
                          <td className="px-4 py-3 report-td-center text-slate-700">
                            {formatNumber(row.rebooked_count, 0)}
                          </td>
                          <td className="px-4 py-3 report-td-center text-slate-700">
                            {formatPercent(row.rebooking_rate_percent)}
                          </td>
                        </tr>
                      ))}
                    {!rebookingSort.sortedRows.length && (
                      <tr>
                        <td colSpan={4} className="px-4 py-6 text-center text-slate-500">
                          {loading ? "Loading..." : "No rebooking data found."}
                        </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          {activeTab === "cohortRetention" && (
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6 text-slate-600">
              Cohort retention is available as a follow-up enhancement.
            </div>
          )}
          {activeTab === "targetLists" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6 space-y-3">
                <h3 className="text-lg font-semibold text-slate-900">Clients by Service</h3>
                <p className="text-slate-600">
                  Use the existing Clients by Service report with your current filters.
                </p>
                <button type="button" onClick={goToClientsByService} className={BTN_PRIMARY}>
                  Open Clients by Service
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
  </div>
  );
}


